import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useData, computeName } from '../contexts/DataContext';
import { uploadFile } from '../services/publitio';
import { getProfileImageUrl } from '../utils/profileImage';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiLock, FiPhone, FiCalendar, FiSave, FiUpload, FiLogOut } = FiIcons;

// Team ID options
const TEAM_IDS = [
  { id: 'emd_rodriguez', name: 'EMD Rodriguez' },
  { id: 'md_garcia', name: 'MD Garcia' },
  { id: 'md_samaniego', name: 'MD Samaniego' }
];

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const sanitizeNamePart = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  const trimmed = String(value).trim();
  return trimmed;
};

const splitFullName = (fullName) => {
  if (!fullName) {
    return { first: '', middle: '', last: '' };
  }

  const parts = String(fullName)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return { first: '', middle: '', last: '' };
  }

  if (parts.length === 1) {
    return { first: parts[0], middle: '', last: '' };
  }

  if (parts.length === 2) {
    return { first: parts[0], middle: '', last: parts[1] };
  }

  const first = parts.shift();
  const last = parts.pop();
  const middle = parts.join(' ');

  return {
    first: first ?? '',
    middle,
    last: last ?? ''
  };
};

const getStructuredFields = (record) => {
  const structured = record?.structuredFields ?? record?.structured_fields;
  return isPlainObject(structured) ? structured : {};
};

const getUserNameDetails = (record) => {
  if (!record) {
    return { firstName: '', middleName: '', lastName: '', fullName: '' };
  }

  const structured = getStructuredFields(record);

  let firstName = sanitizeNamePart(
    record?.firstName ?? structured.firstName ?? record?.first_name ?? structured.first_name
  );
  let middleName = sanitizeNamePart(
    record?.middleName ?? structured.middleName ?? record?.middle_name ?? structured.middle_name
  );
  let lastName = sanitizeNamePart(
    record?.lastName ?? structured.lastName ?? record?.last_name ?? structured.last_name
  );

  const fallbackFullName = sanitizeNamePart(
    structured.fullName ?? structured.full_name ?? structured.name ?? record?.name
  );

  if ((!firstName || !lastName) && fallbackFullName) {
    const parsed = splitFullName(fallbackFullName);
    firstName = firstName || parsed.first;
    middleName = middleName || parsed.middle;
    lastName = lastName || parsed.last;
  }

  const fullName = computeName(firstName, middleName, lastName) || fallbackFullName;

  return { firstName, middleName, lastName, fullName };
};

const toSnakeKey = (key) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const buildStructuredNameFields = (baseFields, nameDetails, fullName) => {
  const structured = {
    ...(isPlainObject(baseFields) ? baseFields : {})
  };

  const setOrDelete = (key, value) => {
    const snakeKey = toSnakeKey(key);
    if (value) {
      structured[key] = value;
      if (snakeKey !== key) {
        delete structured[snakeKey];
      }
    } else {
      delete structured[key];
      if (snakeKey !== key) {
        delete structured[snakeKey];
      }
    }
  };

  setOrDelete('firstName', nameDetails.firstName);
  setOrDelete('middleName', nameDetails.middleName);
  setOrDelete('lastName', nameDetails.lastName);
  if (fullName) {
    setOrDelete('fullName', fullName);
    setOrDelete('name', fullName);
  } else {
    setOrDelete('fullName', null);
    setOrDelete('name', null);
  }

  return structured;
};

const ProfileSettings = () => {
  const { user, signOut } = useAuth();
  const { updateUser, addDocument } = useData();
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    agentCode: '',
    teamId: '',
    profileImageId: '',
    profileImageUrl: '',
    avatar: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (user) {
      const nameDetails = getUserNameDetails(user);
      setFormData({
        firstName: nameDetails.firstName,
        middleName: nameDetails.middleName,
        lastName: nameDetails.lastName,
        email: user.email || '',
        phone: user.phone || '',
        dateOfBirth: user.dateOfBirth || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        agentCode: user.agentCode || '',
        teamId: user.teamId || '',
        profileImageId: user.profileImageId || '',
        profileImageUrl: user.profileImageUrl || '',
        avatar: user.avatar || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const upload = await uploadFile(file, 'avatars');
      setImagePreview(upload.url);
      setFormData(prev => ({
        ...prev,
        profileImageId: upload.public_id,
        profileImageUrl: upload.url,
        avatar: ''
      }));
      await addDocument({ name: file.name, clientId: null, publitioId: upload.public_id, url: upload.url });
    } catch (err) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({ ...prev, avatar: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    // Password validation
    if (formData.newPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        setErrorMessage('New passwords do not match.');
        return;
      }
      if (formData.currentPassword !== 'password123') { // Mock password check
        setErrorMessage('Current password is incorrect.');
        return;
      }
    }

    try {
      // Update the user data
      const nameDetails = {
        firstName: sanitizeNamePart(formData.firstName),
        middleName: sanitizeNamePart(formData.middleName),
        lastName: sanitizeNamePart(formData.lastName)
      };
      const computedFullName = computeName(
        nameDetails.firstName,
        nameDetails.middleName,
        nameDetails.lastName
      );
      const existingNameDetails = getUserNameDetails(user);
      const finalFullName = computedFullName || existingNameDetails.fullName || '';
      const baseStructuredFields = getStructuredFields(user);
      const structuredFields = buildStructuredNameFields(baseStructuredFields, nameDetails, finalFullName);

      const updatedData = {
        name: finalFullName,
        email: formData.email,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        agent_code: formData.agentCode,
        team_id: formData.teamId,
      };

      if (Object.keys(structuredFields).length > 0) {
        updatedData.structuredFields = structuredFields;
      }

      if (formData.profileImageId || formData.profileImageUrl) {
        updatedData.profileImageId = formData.profileImageId;
        updatedData.profileImageUrl = formData.profileImageUrl;
        updatedData.avatar = null;
      } else if (formData.avatar) {
        updatedData.avatar = formData.avatar;
      }
      
      updateUser(user.id, updatedData);
      setSuccessMessage('Profile updated successfully.');
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
    } catch (error) {
      setErrorMessage('Failed to update profile. Please try again.');
      console.error('Profile update error:', error);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6">
            <h1 className="text-3xl font-heading font-bold text-gray-900">Profile Settings</h1>
            <p className="text-gray-600">Manage your account information and preferences</p>
          </div>

          {successMessage && (
            <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700">
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Picture */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-xl font-semibold text-gray-900">Profile Picture</h2>
              </div>
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <img
                    src={imagePreview || getProfileImageUrl(user)}
                    alt={getUserNameDetails(user).fullName || user?.email || 'Profile'}
                    className="w-32 h-32 rounded-full object-cover"
                  />
                  <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                    <SafeIcon icon={FiUpload} className="w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500">Click the upload button to change your profile picture</p>
              </div>
            </div>

            {/* Personal Information */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="form-input pl-10"
                      placeholder="Your first name"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Middle Name
                  </label>
                  <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Your middle name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Your last name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input pl-10"
                      placeholder="Your email address"
                      readOnly
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiPhone} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-input pl-10"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiCalendar} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="form-input pl-10"
                    />
                  </div>
                </div>
                
                {/* Agent Code - Only for admin, manager, and financial professional */}
                {user?.role !== 'client' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Agent Code
                    </label>
                    <input
                      type="text"
                      name="agentCode"
                      value={formData.agentCode}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Your agent code"
                    />
                  </div>
                )}

                {/* Team Selection - Only for admin, manager, and financial professional */}
                {user?.role !== 'client' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Team
                    </label>
                    <select
                      name="teamId"
                      value={formData.teamId}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value="">Select team</option>
                      {TEAM_IDS.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Password Change */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      className="form-input pl-10"
                      placeholder="Enter your current password"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className="form-input pl-10"
                      placeholder="Enter new password"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="form-input pl-10"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                Leave password fields empty if you don't want to change your password.
              </p>
            </div>

            {/* Submit and Sign Out Buttons */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleSignOut}
                className="btn-danger flex items-center space-x-2"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
              <button
                type="submit"
                className="btn-primary flex items-center space-x-2"
              >
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfileSettings;